import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal, ViewEncapsulation } from '@angular/core';
import { ButtonComponent, ButtonDisplayStyle, ButtonSize } from '@workbuddy/ui/components/button';
import { PAGE_COMPONENTS, PageHeaderSize, PageMode } from '@workbuddy/ui/blocks/page';
import { ActionBarComponent } from '@workbuddy/ui/v2/components/action.bar';
import type { FieldError, FieldMode } from '../../core/contracts/field.binding';
import type { SurfaceContextHandle } from '../../core/runtime/surface.context/create.surface.context.options';
import { WbPanelComponent } from '../../core/panel/wb.panel.component';
import { WbSectionComponent } from '../../core/sections/wb.section.component';
import { resolveSurfaceActions, toSurfaceActionCommands } from '../../core/surface/surface.action.presenter';
import { buildSectionRenderMap, layoutSurfaceSections, visibleSectionsForPaths } from '../../core/surface/surface.section.presenter';
import { mountCreatorHostTheme } from './wb.surface.creator.host.theme';

@Component({
  selector: 'wb-surface-creator-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [PAGE_COMPONENTS, ButtonComponent, ActionBarComponent, WbPanelComponent, WbSectionComponent],
  templateUrl: './wb.surface.creator.host.component.html',
  styleUrls: ['./wb.surface.creator.host.component.less'],
  host: {
    '[attr.aria-busy]': '$busy()',
    '[attr.data-surface]': '"creator"',
    '[attr.data-section-border]': '$sectionBorder() ? "true" : "false"',
    '[attr.data-column-divider]': '$columnDivider() ? "true" : "false"',
  },
})
export class WbSurfaceCreatorHostComponent {

  private readonly document = inject(DOCUMENT);

  protected readonly PageMode = PageMode;
  protected readonly PageHeaderSize = PageHeaderSize;
  protected readonly ButtonDisplayStyle = ButtonDisplayStyle;
  protected readonly ButtonSize = ButtonSize;

  public readonly $handle = input.required<SurfaceContextHandle>({ alias: 'handle' });

  public constructor() {

    mountCreatorHostTheme(this.document);
  }


  protected readonly $submitting = signal(false);
  private readonly $submitErrors = signal<readonly FieldError[]>([]);

  protected readonly $page = computed(() => this.$handle().page);
  protected readonly $contentMaxWidth = computed(() => {
    const maxWidth = this.$handle().sectionLayout.maxWidth;
    return maxWidth === null ? 'none' : `${maxWidth}px`;
  });
  protected readonly $sectionBorder = computed(() => this.$handle().sectionLayout.sectionBorder);
  protected readonly $columnDivider = computed(() => this.$handle().sectionLayout.columnDivider);
  protected readonly $surfaceMode = computed<FieldMode>(() => this.$handle().surface.defaultFieldMode);
  // Creator fields use defaultFieldMode `edit` (they are writable). Chrome is the operation: this host is create-only.
  protected readonly $title = computed(() => `New ${this.$handle().compiled.entity ?? 'Record'}`);
  protected readonly $submitLabel = computed(() => 'Create');
  protected readonly $busy = computed(() => this.$page().$initializing() || this.$submitting() || this.$page().$saving());
  protected readonly $createDisabled = computed(() => this.$busy() || this.$page().$invalid() || this.$page().$validating());
  protected readonly $errors = computed<readonly FieldError[]>(() => [
    ...this.$page().$surfaceErrors().map(error => ({ code: error.code, message: error.message })),
    ...this.$submitErrors(),
  ]);

  private readonly $sectionRenderInfoByPath = computed(() => {
    return buildSectionRenderMap(this.$handle());
  });

  protected readonly $identitySections = computed(() => visibleSectionsForPaths(this.$handle().compiled.namedSlotSections['identity'] ?? [], this.$sectionRenderInfoByPath(), this.$page()));
  protected readonly $bodySections = computed(() => visibleSectionsForPaths(this.$handle().compiled.bodySections, this.$sectionRenderInfoByPath(), this.$page()));
  protected readonly $bodyLayout = computed(() => layoutSurfaceSections(this.$bodySections(), this.$handle().sectionLayout.columns, this.$handle().compiled.sectionColumns?.['body']));

  private readonly $resolvedCommands = computed(() => resolveSurfaceActions(this.$handle()));

  protected readonly $actionCommands = computed(() => toSurfaceActionCommands(this.$resolvedCommands(), this.$busy()));

  protected cancel(): void {

    if (this.$busy()) return;
    this.$page().cancel();
  }

  protected async create(): Promise<void> {

    if (this.$createDisabled()) return;
    this.$submitting.set(true);
    this.$submitErrors.set([]);
    try {
      const result = await this.$page().submit();
      if (!result.ok) this.$submitErrors.set(result.errors ?? []);
    } finally {
      this.$submitting.set(false);
    }
  }

}
